/**
 * Full-screen analysing state for suggestion engines (Aged Care / Support at Home).
 * Portals to document.body so position:fixed is not clipped by parent layout/transform.
 */
import React, { useId } from 'react';
import { createPortal } from 'react-dom';

export const SUGGESTION_ENGINE_MIN_ANALYSIS_DISPLAY_MS = 5000;

interface SuggestionEngineAnalysisLoaderProgress {
    current: number;
    total: number;
}

interface SuggestionEngineAnalysisLoaderProps {
    title?: string;
    subtitle?: string;
    progress?: SuggestionEngineAnalysisLoaderProgress;
    progressLabel?: string;
}

export default function SuggestionEngineAnalysisLoader({
    title = 'Optimising your funding strategies',
    subtitle = "We're working through a large number of simulated options - this may take a few minutes.",
    progress = { current: 0, total: 0 },
    progressLabel = 'Running strategy',
}: SuggestionEngineAnalysisLoaderProps) {
    const gradientId = useId().replace(/:/g, '');

    const loader = (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-white border border-slate-200 "
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-label={title}
        >
            <div className="text-center max-w-lg px-6 py-8">
                <div className="mb-8 flex justify-center shrink-0">
                    <svg width="240" height="140" viewBox="0 0 240 140" fill="none" className="text-blue-600/90" aria-hidden>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
                                <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="currentColor" stopOpacity="0.9" />
                            </linearGradient>
                        </defs>
                        <rect x="40" y="70" width="28" height="50" rx="4" fill={`url(#${gradientId})`} className="animate-pulse" style={{ animationDuration: '1.5s' }} />
                        <rect x="80" y="50" width="28" height="70" rx="4" fill={`url(#${gradientId})`} className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.2s' }} />
                        <rect x="120" y="35" width="28" height="85" rx="4" fill={`url(#${gradientId})`} className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.4s' }} />
                        <rect x="160" y="55" width="28" height="65" rx="4" fill={`url(#${gradientId})`} className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.6s' }} />
                        <rect x="200" y="25" width="28" height="95" rx="4" fill={`url(#${gradientId})`} className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.8s' }} />
                        <path
                            d="M 48 95 Q 88 75 128 60 T 208 45"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                            opacity="0.7"
                            strokeDasharray="6 4"
                            className="animate-pulse"
                            style={{ animationDuration: '2s' }}
                        />
                    </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">{title}</h2>
                <p className="text-gray-600 mb-6">{subtitle}</p>
                {progress.total > 0 && (
                    <p className="text-sm text-blue-700 font-medium">
                        {progressLabel} {progress.current} of {progress.total}
                    </p>
                )}
                <div className="mt-6 flex justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-200 border-t-blue-600" role="status" aria-label="Loading" />
                </div>
            </div>
        </div>
    );

    if (typeof document !== 'undefined' && document.body) {
        return createPortal(loader, document.body);
    }

    return loader;
}
